import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import UnifiedModal from '../UnifiedModal';
import { Form } from 'react-bootstrap';
import userProfileService from '../../services/userProfileService';

function BookingNumberModal({ dir, shouldShow, setModalVisible, cid, item, searchKey }) {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [bookingNumber, setBookingNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const getPlaceholder = (key) => {
    if (key === 'argus') return 'EU123456789';
    if (key === 'rentalcars21') return '123456789';
    if (key === 'discover') return 'D0123456789';
    return '123456789';
  };

  const placeholder = item ? getPlaceholder(item.key) : '123456789';

  const handleClose = useCallback(() => {
    setModalVisible(false);
    // Reset state when closing
    setBookingNumber('');
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(false);
  }, [setModalVisible]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !user.id) {
      setSubmitError(t('bookingNumberModalErrorNotLoggedIn') || 'Please log in to submit booking numbers');
      return;
    }

    if (!bookingNumber.trim()) {
      setSubmitError(t('bookingNumberModalErrorEmpty') || 'Please enter a booking number');
      return;
    }

    if (!searchKey) {
      setSubmitError(t('bookingNumberModalErrorNoSearch') || 'No search data available');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const result = await userProfileService.addMonitoredBooking(
        user.id,
        bookingNumber.trim(),
        searchKey
      );

      if (result) {
        setSubmitSuccess(true);
        setBookingNumber('');
        // Close modal after a short delay to show success message
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSubmitError(t('bookingNumberModalErrorGeneric') || 'Failed to submit booking number. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting booking number:', error);
      setSubmitError(
        error.message || 
        t('bookingNumberModalErrorGeneric') || 
        'Failed to submit booking number. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerButtons = [
    {
      variant: 'secondary',
      onClick: handleClose,
      children: t("cancel"),
      disabled: isSubmitting
    },
    {
      variant: 'primary',
      onClick: handleSubmit,
      children: isSubmitting ? (t("submitting") || "Submitting...") : t("submit"),
      disabled: isSubmitting || !bookingNumber.trim()
    }
  ];

  return (
    <UnifiedModal
      show={shouldShow}
      onHide={handleClose}
      title={t("bookingNumberModalTitle")}
      dir={dir}
      footerButtons={footerButtons}
    >
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="bookingNumber">
          <Form.Label>{t("bookingNumberModalLabel")}</Form.Label>
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={bookingNumber}
            onChange={(e) => setBookingNumber(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </Form.Group>
        
        {submitError && (
          <div className="alert alert-danger mt-3" role="alert">
            {submitError}
          </div>
        )}
        
        {submitSuccess && (
          <div className="alert alert-success mt-3" role="alert">
            {t("bookingNumberModalSuccess") || "Booking number submitted successfully! We'll monitor prices for you."}
          </div>
        )}
      </Form>
    </UnifiedModal>
  );
}

export default BookingNumberModal;